import axios from 'axios';
import { emitAuthExpired } from './authEvents';
import { getFirebaseAuth, isFirebaseClientConfigured } from './firebaseClient';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AUTH_ME_SEGMENT = '/api/auth/me';

const PUBLIC_PATHS = new Set(['/', '/login', '/register', '/forgot-password', '/reset-password']);

function requestUrl(config) {
  if (!config) return '';
  const base = config.baseURL || '';
  const u = config.url || '';
  if (u.startsWith('http')) return u;
  return `${base.replace(/\/$/, '')}/${u.replace(/^\//, '')}`;
}

function isAuthMeRequest(config) {
  return requestUrl(config).includes(AUTH_ME_SEGMENT);
}

function shouldRedirectOnUnauthorized() {
  if (typeof window === 'undefined') return false;
  const path = window.location.pathname || '/';
  return !PUBLIC_PATHS.has(path);
}

async function attachFirebaseIdToken(config, forceRefresh = false) {
  if (!isFirebaseClientConfigured()) return config;
  try {
    const auth = getFirebaseAuth();
    const u = auth.currentUser;
    if (!u) return config;
    const token = await u.getIdToken(forceRefresh);
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  } catch {
    /* leave request unauthenticated */
  }
  return config;
}

export const apiClient = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

let cookieRefreshRequest;
function renewCookieSession() {
  if (!cookieRefreshRequest) {
    // Plain axios avoids recursively intercepting an expired refresh cookie.
    cookieRefreshRequest = axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true })
      .finally(() => { cookieRefreshRequest = null; });
  }
  return cookieRefreshRequest;
}

apiClient.interceptors.request.use(
  async (config) => attachFirebaseIdToken(config, false),
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    const status = error.response?.status;

    if (status !== 401 || !config) {
      return Promise.reject(error);
    }

    if (!config.__ihFirebaseRetry && isFirebaseClientConfigured()) {
      try {
        const auth = getFirebaseAuth();
        if (auth.currentUser) {
          config.__ihFirebaseRetry = true;
          await attachFirebaseIdToken(config, true);
          return apiClient.request(config);
        }
      } catch {
        /* fall through */
      }
    }

    if (!config.__ihCookieRetry && !config.headers?.Authorization && !requestUrl(config).includes('/api/auth/refresh')) {
      config.__ihCookieRetry = true;
      try {
        await renewCookieSession();
        return apiClient.request(config);
      } catch { /* Keep the existing unauthorized path when refresh has expired. */ }
    }

    if (isAuthMeRequest(config)) {
      return Promise.reject(error);
    }

    emitAuthExpired();
    if (shouldRedirectOnUnauthorized()) {
      window.location.assign('/login');
    }
    return Promise.reject(error);
  }
);

/**
 * Shared Firebase auth interceptors for axios instances (e.g. engine API under /api/engine).
 */
export function applyFirebaseAuthInterceptors(instance) {
  instance.interceptors.request.use(
    async (config) => attachFirebaseIdToken(config, false),
    (err) => Promise.reject(err)
  );
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const config = error.config;
      const status = error.response?.status;
      if (status !== 401 || !config) {
        return Promise.reject(error);
      }
      if (!config.__ihFirebaseRetry && isFirebaseClientConfigured()) {
        try {
          const auth = getFirebaseAuth();
          if (auth.currentUser) {
            config.__ihFirebaseRetry = true;
            await attachFirebaseIdToken(config, true);
            return instance.request(config);
          }
        } catch {
          /* fall through */
        }
      }
      emitAuthExpired();
      if (shouldRedirectOnUnauthorized()) {
        window.location.assign('/login');
      }
      return Promise.reject(error);
    }
  );
}

export default apiClient;

// Native media elements cannot attach Firebase bearer headers. Renew the existing
// app session before assigning their URLs; never put credentials in media URLs.
let mediaSessionRequest;
export function refreshMediaSession() {
  if (!mediaSessionRequest) {
    mediaSessionRequest = (async () => {
      const auth = isFirebaseClientConfigured() ? getFirebaseAuth() : null;
      if (auth?.currentUser) {
        const idToken = await auth.currentUser.getIdToken();
        await apiClient.post('/api/auth/firebase', { id_token: idToken });
      } else {
        await renewCookieSession();
      }
    })().finally(() => { mediaSessionRequest = null; });
  }
  return mediaSessionRequest;
}
